import { useDispatch, useSelector } from 'react-redux';
import {
    setVisibleDateRange,
    setFilters,
    setSelectedDate,
} from 'src/pages/home/redux/actions';
import { convertDateToISOString } from 'src/utils/util';
import {
    getEndDateForStartDate,
    getEndOfTimeDate,
    isStartDateLessThanEndDate,
} from 'src/utils/dateUtils';
import { CALENDAR_VIEW } from 'src/constants/appConstants';

export const useCalendar = () => {
    const dispatch = useDispatch();
    const selectedDate = useSelector((state) => state.filterReducer.selectedDate);
    const calendarView = useSelector((state) => state.filterReducer.calendarView);
    const visibleDateRange = useSelector((state) => state.filterReducer.visibleDateRange);
    const surgeryDates = useSelector((state) => state.ordersReducer.viewData.surgeryDates);
    const orderSummaryModal = useSelector((state) => state.orderSummaryReducer.orderSummaryModal);
    const callOrderOrCalendar = useSelector((state) => state.updateSurgeryReducer.callOrderOrCalendar);

    const setComponentVisibleDateRange = () => {
        const selectedDateFormatted = new Date(selectedDate);
        const currentDay = selectedDateFormatted.getDay();
        if (calendarView === CALENDAR_VIEW.WEEK) {
            let startWeekDate = new Date(selectedDateFormatted);
            let endWeekDate = new Date(selectedDateFormatted);
            startWeekDate.setDate(selectedDateFormatted.getDate() - currentDay);
            startWeekDate = new Date(startWeekDate.setHours(0, 0, 0, 0));
            endWeekDate.setDate(selectedDateFormatted.getDate() + (6 - currentDay));
            endWeekDate = new Date(endWeekDate.setHours(23, 59, 59, 999));
            if (
                startWeekDate.getTime() !== visibleDateRange[0]?.getTime() ||
                endWeekDate.getTime() !== visibleDateRange[1]?.getTime()
            ) {
                dispatch(setVisibleDateRange([startWeekDate, endWeekDate]));
            }
        } else if (calendarView === CALENDAR_VIEW.MONTH) {
            const startMonthDate = new Date(
                selectedDateFormatted.getFullYear(),
                selectedDateFormatted.getMonth(),
                1,
                0,
                0,
                0
            );
            const endMonthDate = new Date(
                selectedDateFormatted.getFullYear(),
                selectedDateFormatted.getMonth() + 1,
                0,
                23,
                59,
                59
            );
            if (
                startMonthDate.getTime() !== visibleDateRange[0]?.getTime() ||
                endMonthDate.getTime() !== visibleDateRange[1]?.getTime()
            ) {
                dispatch(setVisibleDateRange([startMonthDate, endMonthDate]));
            }
        }
    };

    const changeSelectedDate = (selectedDate) => {
        const isFutureOrToday =
            new Date(selectedDate).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
        let newEndDate;
        if (isFutureOrToday) {
            newEndDate = getEndOfTimeDate();
        } else if (calendarView === CALENDAR_VIEW.WEEK) {
            // Extend endDate to end of the current week (Saturday) so that
            // userFilters.endDate always covers the full visible week range.
            // Without this, endDate is only the clicked day, and when the user
            // navigates to the next week startDate jumps +7 days while endDate
            // stays behind → inverted startDate > endDate sent to the API.
            const dayOfWeek = new Date(selectedDate).getDay();
            const daysUntilSaturday = 6 - dayOfWeek;
            newEndDate = getEndDateForStartDate(new Date(selectedDate), daysUntilSaturday);
        } else {
            newEndDate = getEndDateForStartDate(new Date(selectedDate), 0);
        }
        const startDate = convertDateToISOString(new Date(selectedDate), 'start');
        const endDate = convertDateToISOString(new Date(newEndDate), 'end');
        if (isStartDateLessThanEndDate(startDate, endDate)) {
            dispatch(setFilters({ startDate, endDate }));
        }
        dispatch(setSelectedDate(new Date(selectedDate.setHours(0, 0, 0, 0))));
    };

    return {
        selectedDate,
        calendarView,
        visibleDateRange,
        surgeryDates,
        orderSummaryModal,
        callOrderOrCalendar,
        setComponentVisibleDateRange,
        changeSelectedDate,
    };
};
